import { useEffect, useRef } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useCreateGiftVoucherSaleMutation, useUpdateGiftVoucherSaleMutation } from '../services/giftVoucherApi';
import { setGvServerSale, setGvSyncError, selectGvSale } from '../store/giftVoucherSaleSlice';
import { draftPayload, EMAIL_RE, typeAllowsValue } from '../components/pos/giftVoucher/gvUtils';

// Saves the gift-voucher draft to the server (create, then PATCH) shortly after
// the cashier stops editing. Only runs while Gift Voucher mode is active and
// the draft is valid enough for the API (buyer + email + at least one valid line).
export function useGiftVoucherDraftSync({ active, types }) {
  const dispatch = useDispatch();
  const sale = useSelector(selectGvSale);
  const [createSale] = useCreateGiftVoucherSaleMutation();
  const [updateSale] = useUpdateGiftVoucherSaleMutation();
  const timer = useRef(null);
  const inFlight = useRef(false);

  const ready = !!sale.buyer && EMAIL_RE.test(sale.buyerEmail.trim()) && sale.lines.length > 0
    && sale.lines.every((l) => Number(l.qty) >= 1 && typeAllowsValue((types || []).find((t) => t.id === l.typeId), l.value));

  useEffect(() => {
    if (!active || !sale.dirty || !ready) return undefined;
    clearTimeout(timer.current);
    timer.current = setTimeout(async () => {
      if (inFlight.current) return;
      inFlight.current = true;
      const rev = sale.rev;
      try {
        const body = draftPayload(sale);
        const res = sale.saleId
          ? await updateSale({ id: sale.saleId, ...body }).unwrap()
          : await createSale(body).unwrap();
        dispatch(setGvServerSale({ sale: res?.data?.gift_voucher_sale, approvalsRequired: res?.data?.approvals_required, rev }));
      } catch (err) {
        dispatch(setGvSyncError(err?.data?.error || 'Could not save the voucher sale'));
      } finally {
        inFlight.current = false;
      }
    }, 500);
    return () => clearTimeout(timer.current);
    // eslint-disable-next-line
  }, [active, sale.dirty, sale.rev, sale.server, ready]);

  return { ready };
}
